import { useMemo } from 'react';
import { TabItem } from '../../components/components.interfaces';
import { PageName } from '../../../utils/pages-helpers';
import { ProfileCalendarProvider } from './contexts/profileCalendar.context';
import ProfileCalendarView from './subviews/profileCalendar/profileCalendar.view';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import PersonalDetailsView from './subviews/personalDetails/personalDetails.view';
import { ProfilePersonalDetailsProvider } from './contexts/profilePersonalDetails.context';
import { ProfileDocumentsProvider } from './contexts/profileDocuments.context';
import ProfileDocumentsView from './subviews/documents/profileDocuments.view';
import ProfileLeavesView from './subviews/leaves/profileLeaves.view';
import { useRbacState } from 'src/context/rbac.context';
import { hasReadPermission } from 'src/utils/rbacHelpers';
import { PermissionGate } from '../../../components/rbac/PermissionGate';
import { RbacDeniedRedirect } from '../../../components/rbac/RbacDeniedRedirect';

export const profileSubRouteIds = [
  'personal-details',
  PageName.documents,
  PageName.calendar,
  PageName.leaves,
];

export const getProfileTabIndex = (pathname: string) => {
  const profileTabsIndexObject = {
    'personal-details': 0,
    [PageName.documents]: 1,
    [PageName.calendar]: 2,
    [PageName.leaves]: 3,
  };

  const tabIndex =
    profileTabsIndexObject[pathname as keyof typeof profileTabsIndexObject];
  return tabIndex ?? 0;
};

export const getProfileSubRouteId = (index: number) =>
  profileSubRouteIds[index];

export const useProfileTabMenuItems = (
  translateText: TTranslateTextType
): TabItem[] => {
  const { rbac } = useRbacState();

  return useMemo(() => {
    const tabs: TabItem[] = [];

    if (hasReadPermission(rbac, 'profile', 'personalDetails')) {
      tabs.push({
        label: translateText('profile.tabs.personalDetails'),
        onClickHandler: () => {},
        tabPanel: (
          <ProfilePersonalDetailsProvider>
            <PersonalDetailsView />
          </ProfilePersonalDetailsProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'profile', 'documents')) {
      tabs.push({
        label: translateText('profile.tabs.documents'),
        onClickHandler: () => {},
        tabPanel: (
          <ProfileDocumentsProvider>
            <ProfileDocumentsView />
          </ProfileDocumentsProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'profile', 'calendar')) {
      tabs.push({
        label: translateText('profile.tabs.calendar'),
        onClickHandler: () => {},
        tabPanel: (
          <ProfileCalendarProvider>
            <ProfileCalendarView />
          </ProfileCalendarProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'profile', 'leaves')) {
      tabs.push({
        label: translateText('profile.tabs.leaves'),
        onClickHandler: () => {},
        tabPanel: (
          <PermissionGate
            module="profile"
            submodule="leaves"
            permission="any"
            fallback={<RbacDeniedRedirect />}
          >
            <ProfileLeavesView />
          </PermissionGate>
        ),
      });
    }

    return tabs;
  }, [rbac, translateText]);
};

/** Returns route IDs for tabs the user has read permission for, in display order */
export const useProfileAvailableRouteIds = (): string[] => {
  const { rbac } = useRbacState();
  return useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'profile', 'personalDetails'))
      routes.push('personal-details');
    if (hasReadPermission(rbac, 'profile', 'documents'))
      routes.push(PageName.documents);
    if (hasReadPermission(rbac, 'profile', 'calendar'))
      routes.push(PageName.calendar);
    if (hasReadPermission(rbac, 'profile', 'leaves')) routes.push(PageName.leaves);
    return routes;
  }, [rbac]);
};
